import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";

/**
 * Dialog per inviare un Alert/Richiamo su un cliente.
 * Props:
 *  - open, onOpenChange
 *  - clientId (required)
 *  - clientName (opzionale, per il titolo)
 *  - prefillText: testo pre-compilato (es. da criticità)
 *  - source: "task" | "criticality" | "manual"
 *  - sourceRef: ID riferimento
 *  - sourceLabel: descrizione umana dell'origine
 *  - onSent: callback dopo invio andato a buon fine
 */
export default function AlertRichiamoDialog({
  open,
  onOpenChange,
  clientId,
  clientName = "",
  prefillText = "",
  source = "manual",
  sourceRef = "",
  sourceLabel = "",
  onSent,
}) {
  const [text, setText] = useState(prefillText || "");
  const [priority, setPriority] = useState("media");
  const [recipients, setRecipients] = useState([]); // {user_id, name, email, role_label}
  const [selected, setSelected] = useState([]);     // array di user_id
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setText(prefillText || "");
      setPriority("media");
      setSelected([]);
    }
  }, [open, prefillText]);

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/clients/${clientId}/alert-recipients`);
        if (res.ok && !cancelled) {
          const d = await res.json();
          setRecipients(d.recipients || []);
        }
      } catch (e) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  const toggle = (uid) => {
    setSelected(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);
  };

  const handleSend = async () => {
    if (!text.trim()) { toast.error("Scrivi il messaggio del richiamo"); return; }
    if (selected.length === 0) { toast.error("Seleziona almeno un destinatario"); return; }
    setSending(true);
    try {
      const res = await apiFetch(`/clients/${clientId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: text.trim(),
          is_alert: true,
          alert_priority: priority,
          alert_recipient_user_ids: selected,
          alert_source: source,
          alert_source_ref: sourceRef || null,
          alert_source_label: sourceLabel || null,
        }),
      });
      if (res.ok) {
        toast.success(`Alert inviato a ${selected.length} destinatario/i (email + notifica in-app)`);
        onOpenChange?.(false);
        onSent?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore invio alert");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setSending(false);
    }
  };

  const priorityMeta = {
    bassa: {
      label: "Bassa",
      color: "text-[#0f7040]",
      bg: "bg-[#e7f7ee]",
      border: "border-[#b5e5cb]",
      dot: "bg-[#10b981]",
      activeRing: "border-[#10b981] bg-[#f0fbf5] ring-2 ring-[#10b981]/20",
    },
    media: {
      label: "Media",
      color: "text-[#b45309]",
      bg: "bg-[#fef3c7]",
      border: "border-[#fcd34d]",
      dot: "bg-[#f59e0b]",
      activeRing: "border-[#f59e0b] bg-[#fffbeb] ring-2 ring-[#f59e0b]/20",
    },
    alta: {
      label: "Alta",
      color: "text-[#c0392b]",
      bg: "bg-[#fdecea]",
      border: "border-[#f5b7b1]",
      dot: "bg-[#e02424]",
      activeRing: "border-[#e02424] bg-[#fef2f2] ring-2 ring-[#e02424]/20",
    },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[540px] p-0 overflow-hidden rounded-[22px] border border-[#e9e9f1] shadow-[0_20px_60px_rgba(15,23,42,0.18)] bg-white scheda-font-ui max-h-[92vh] flex flex-col duration-200"
        data-testid="alert-dialog"
      >
        {/* Header pulito con icona nel badge coerente */}
        <div className="px-6 pt-6 pb-4 border-b border-[#f0f0f6] bg-[#fcfcfd]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[12px] bg-[#fdecea] flex items-center justify-center shrink-0 shadow-xs">
              <AlertTriangle className="w-5 h-5 text-[#c0392b]" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-[17px] font-bold text-[#15161c] leading-tight">Segnala Problema / Alert</h2>
                {clientName && (
                  <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full bg-[#eeeefc] text-[#5b5bd6] truncate max-w-[180px]">
                    {clientName}
                  </span>
                )}
              </div>
              <p className="text-[12.5px] text-[#9095a1] mt-0.5">
                Crea una segnalazione prioritaria per il team del cliente
              </p>
            </div>
          </div>
        </div>

        {/* Corpo scrollabile */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          {sourceLabel && (
            <div className="p-3 rounded-[12px] bg-[#fbfbfd] border border-[#e9e9f1] text-[12.5px] text-[#565d6d] flex items-start gap-2" data-testid="alert-source-label">
              <span className="font-semibold text-[#15161c] shrink-0">Origine:</span>
              <span className="italic truncate">{sourceLabel}</span>
            </div>
          )}

          {/* Selezione Priorità */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[.08em] text-[#9095a1] block mb-2">
              Priorità segnalazione
            </label>
            <RadioGroup value={priority} onValueChange={setPriority} className="grid grid-cols-3 gap-2.5" data-testid="alert-priority-group">
              {(["bassa", "media", "alta"]).map(p => {
                const m = priorityMeta[p];
                const active = priority === p;
                return (
                  <label
                    key={p}
                    htmlFor={`priority-${p}`}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-[12px] border text-[13px] cursor-pointer transition-all duration-150 select-none ${
                      active
                        ? `${m.activeRing} font-bold ${m.color} shadow-xs`
                        : "border-[#e9e9f1] bg-white text-[#565d6d] hover:border-[#d9d9e6] hover:bg-[#fafafc]"
                    }`}
                    data-testid={`priority-${p}`}
                  >
                    <RadioGroupItem value={p} id={`priority-${p}`} className="sr-only" />
                    <span className={`w-2.5 h-2.5 rounded-full ${m.dot} shrink-0`} />
                    <span>{m.label}</span>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          {/* Selezione Destinatari */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-bold uppercase tracking-[.08em] text-[#9095a1]">
                Destinatari (email + notifica)
              </label>
              {recipients.length > 0 && (
                <span className="text-[11.5px] text-[#9095a1] font-medium">
                  {selected.length}/{recipients.length} selezionati
                </span>
              )}
            </div>

            {loading ? (
              <div className="p-4 rounded-[14px] border border-dashed border-[#e4e4ed] bg-[#fafafc] text-center">
                <p className="text-[13px] text-[#9095a1]">Caricamento destinatari del team…</p>
              </div>
            ) : recipients.length === 0 ? (
              <div className="p-4 rounded-[14px] border border-dashed border-[#e4e4ed] bg-[#fafafc] text-center">
                <p className="text-[13px] text-[#9095a1] italic">
                  Nessun destinatario configurato nel team per questo cliente.
                </p>
              </div>
            ) : (
              <div className="p-3 rounded-[14px] border border-[#eeeef5] bg-[#fafafc] flex flex-wrap gap-2">
                {recipients.map(r => {
                  const active = selected.includes(r.user_id);
                  return (
                    <button
                      key={r.user_id}
                      type="button"
                      onClick={() => toggle(r.user_id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-all border ${
                        active
                          ? "bg-[#eeeefc] text-[#5b5bd6] border-[#c9c9f0] shadow-xs"
                          : "bg-white text-[#565d6d] border-[#e4e4ed] hover:border-[#cfcfe2]"
                      }`}
                      data-testid={`alert-recipient-${r.user_id}`}
                    >
                      <span
                        className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 overflow-hidden ${
                          active ? "bg-[#5b5bd6] text-white" : "bg-[#f0f0f6] text-[#71717a]"
                        }`}
                      >
                        {active ? (
                          "✓"
                        ) : r.picture ? (
                          <img src={r.picture} alt={r.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          (r.name || "?").charAt(0)
                        )}
                      </span>
                      <span className="font-semibold">{r.name}</span>
                      {r.role_label && (
                        <span className="text-[11.5px] opacity-60">({r.role_label})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Textarea Descrizione Problema */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[.08em] text-[#9095a1] block mb-2">
              Descrivi il problema o la criticità
            </label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Descrivi dettagliatamente il problema (es. blocco sul materiale, criticità CAC, ritardo shooting...)"
              rows={4}
              className="w-full rounded-[12px] border border-[#e4e4ed] bg-white p-3.5 text-[14px] leading-[1.6] placeholder:text-[#9095a1] focus-visible:ring-0 focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.12)] transition-all resize-none"
              data-testid="alert-text-input"
            />
            <p className="text-[11.5px] text-[#9095a1] mt-1.5 leading-normal">
              La segnalazione comparirà in cima alle minute con badge ALERT e invierà una notifica immediata ai destinatari.
            </p>
          </div>
        </div>

        {/* Footer con pulsanti stile Scheda Cliente */}
        <div className="px-6 py-4 border-t border-[#f0f0f6] bg-[#fcfcfd] flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            disabled={sending}
            className="h-9 px-4 rounded-[10px] text-[13px] font-semibold text-[#565d6d] hover:bg-[#f1f1f8] transition-colors disabled:opacity-50"
            data-testid="alert-cancel-btn"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !text.trim() || selected.length === 0}
            className="h-9 px-5 rounded-[10px] bg-[#c0392b] hover:bg-[#a93226] text-white text-[13px] font-semibold transition-all inline-flex items-center gap-1.5 shadow-[0_2px_8px_rgba(192,57,43,0.25)] hover:shadow-[0_4px_12px_rgba(192,57,43,0.35)] disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed"
            data-testid="alert-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? "Invio…" : "Invia Segnalazione"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
